import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Avatar, AvatarFallback, AvatarImage } from "@/components/ui/avatar";
import { Edit, Mail, Phone, Settings } from "lucide-react";
import MainLayout from "@/components/layouts";
import { useParams, useNavigate } from "react-router";
import useClientsDetail from "./clients-detail.hooks";
import { useAdministersActiveOrganization } from "@/hooks/useAdministersActiveOrganization";
import { uuid } from "@/interfaces";
import { SkeletonContent } from "@/components/shared/skeleton-content";
import { Button } from "@/components/ui/button";
import { EditClientContact } from "../clients/clients.components/edit-contact-modal";

export default function ClientDetail() {
  const { clientId } = useParams<{ clientId: uuid }>();
  const navigate = useNavigate();

  const { data, loading, error, properties, propertiesLoading, editingModalOpen, setEditingModalOpen, onSubmit } =
    useClientsDetail(clientId as uuid);
  const administersVendor = useAdministersActiveOrganization();

  if (!loading && !data) {
    return (
      <MainLayout breadcrumb={[{ name: "Clients", href: "/clients" }]}>
        <div className="p-6 text-muted-foreground">{error ?? "Client not found."}</div>
      </MainLayout>
    );
  }

  if (loading || !data) {
    return (
      <MainLayout
        breadcrumb={[
          { name: "Clients", href: "/clients" },
          { name: "Loading…", href: "#" },
        ]}
      >
        <SkeletonContent />
      </MainLayout>
    );
  }

  const breadcrumb = [
    { name: "Clients", href: "/clients" },
    { name: data.name, href: "#" },
  ];

  const contact = data.contact;

  return (
    <MainLayout breadcrumb={breadcrumb}>
      <div className="p-6 space-y-6">
        <Card>
          <CardContent>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              <div className="flex items-start gap-4">
                <Avatar className="h-16 w-16">
                  <AvatarImage src={data.image ?? undefined} />
                  <AvatarFallback>{data.name.charAt(0)}</AvatarFallback>
                </Avatar>
                <div className="space-y-1">
                  <h2 className="text-xl font-semibold">{data.name}</h2>
                  <div className="text-muted-foreground">{data.description || "-"}</div>
                  {data.website && (
                    <a href={data.website} target="_blank" className="text-sm text-blue-600 hover:underline">
                      {data.website}
                    </a>
                  )}
                </div>
              </div>

              {contact && (
                <div className="space-y-1">
                  <div className="font-medium text-lg">{contact.name}</div>
                  <div className="text-muted-foreground text-sm">{contact.designation}</div>
                  <div className="text-sm flex items-center mt-4">
                    <Mail className="mr-2 h-4 w-4" strokeWidth={1} /> {contact.email}
                  </div>
                  <div className="text-sm flex items-center">
                    <Phone className="mr-2 h-4 w-4" strokeWidth={1} /> {contact.phone}
                  </div>
                </div>
              )}
            </div>
            <div className="flex items-center gap-4 mt-4">
              <Button
                size="sm"
                variant={"outline"}
                onClick={function () {
                  window.location.href = `mailto:${data.email ?? ""}`;
                }}
              >
                <Mail className="mr-1" size={12} />
                Email
              </Button>
              {administersVendor && (
                <Button size="sm" variant={"outline"} onClick={() => setEditingModalOpen(true)}>
                  <Edit className="mr-1" size={12} />
                  Edit Contact
                </Button>
              )}
            </div>
          </CardContent>
        </Card>

        <Card>
          <CardHeader>
            <CardTitle className="uppercase">Managed Properties</CardTitle>
          </CardHeader>
          {propertiesLoading ? (
            <CardContent>
              <SkeletonContent />
            </CardContent>
          ) : (
            <CardContent className="space-y-2">
              {properties.map((prop) => (
                <div key={prop.id} className="flex items-center gap-3 border p-2 rounded-md">
                  <Avatar className="h-14 w-14 rounded-sm">
                    <AvatarImage src={prop.image ?? undefined} />
                    <AvatarFallback>{prop.name?.[0] || "?"}</AvatarFallback>
                  </Avatar>
                  <div className="flex-1">
                    <div className="font-medium leading-none">{prop.name || "Unknown Property"}</div>
                    <div className="text-muted-foreground text-sm">{prop.address || "-"}</div>
                  </div>
                  <Button size="sm" variant="outline" onClick={() => navigate(`/clients/${clientId}/properties/${prop.id}`)}>
                    <Settings className="mr-1" size={12} />
                    Custom Pricing
                  </Button>
                </div>
              ))}
            </CardContent>
          )}
        </Card>
      </div>
      <EditClientContact
        open={editingModalOpen}
        onOpenChange={setEditingModalOpen}
        onSubmit={onSubmit}
        clientToEdit={data}
      />
    </MainLayout>
  );
}
